import { UserOutlined } from '@ant-design/icons'
import { NetworkStatus } from '@apollo/client'
import { Divider, Input, Spin } from 'antd'
import { cloneDeep } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  ValueVisibilityInput,
  useBasicProfileAboutMeUpdateMutation,
  useGetBasicProfileQuery,
} from '@/generated/graphql'

import { EditButton } from '../profile-item.component'
import s from '../profile.module.less'
import { removeTypeName } from '../profile.util'

interface ProfileSidebarProps {
  userId: number
  isViewer: boolean
  isEdit: boolean
  onEdit: (isEdit: boolean) => void
}

export const ProfileSideBar: React.FC<ProfileSidebarProps> = ({ userId, isViewer, isEdit, onEdit }) => {
  const { t } = useTranslation()
  const [editedAboutMe, setEditedAboutMe] = React.useState<ValueVisibilityInput>()
  const { data, refetch, networkStatus } = useGetBasicProfileQuery({
    variables: {
      userId,
    },
    notifyOnNetworkStatusChange: true,
  })
  const [aboutMeUpdate, { loading: updateLoading }] = useBasicProfileAboutMeUpdateMutation({
    onCompleted: async () => {
      await refetch()
      onEdit(false)
    },
    onError: () => {
      onEdit(false)
    },
  })

  React.useEffect(() => {
    if (data) {
      const basicProfile = cloneDeep(data.user.basicProfile)
      removeTypeName(basicProfile)
      setEditedAboutMe(basicProfile.aboutMe)
    }
  }, [isEdit])

  const loading = updateLoading || networkStatus === NetworkStatus.refetch
  const aboutMe = data?.user.basicProfile.aboutMe
  const getValue = (isEdit: boolean) => (isEdit ? editedAboutMe! : aboutMe!)

  if (!getValue(isEdit)) {
    return null
  }

  const header = (
    <div className={s.sectionHeadContainer}>
      <div className={s.sectionHead}>
        <UserOutlined className={s.sectionIcon} />
        <span className={s.sectionTitle}>{isViewer ? t('profile.basic.aboutMe') : t('profile.basic.aboutsHe')}</span>
      </div>
      <div className={s.sectionHeadExtra}>
        <EditButton
          isViewer={isViewer}
          isEdit={isEdit}
          loading={loading}
          onEdit={onEdit}
          onUpdate={async () => {
            await aboutMeUpdate({ variables: { aboutMe: editedAboutMe! } })
          }}
        />
      </div>
    </div>
  )
  const content = (
    <Input.TextArea
      autoSize={{ minRows: 12 }}
      value={getValue(isEdit).value}
      placeholder={isViewer ? t('profile.basic.aboutMePlaceholder') : t('profile.basic.aboutThem')}
      className={!isEdit ? s.readArea : s.writeArea}
      onChange={(e) => {
        const aboutMe = cloneDeep(editedAboutMe)
        aboutMe!.value = e.target.value
        setEditedAboutMe(aboutMe)
      }}
      readOnly={!isEdit}
    />
  )

  return (
    <div className={s.sidebar}>
      {header}
      <Divider className={s.sidebarItem} />
      {loading ? <Spin className={s.spin} spinning={loading} /> : content}
    </div>
  )
}
